<script lang="ts">
  import { goto, invalidate } from '$app/navigation';
  import RankTierImage from '$lib/er/rank-tiers/rank-tier-image.svelte';
  import LL, { locale } from '$i18n/i18n-svelte';
  import { api } from '$lib/erar/api';
  import { getHumanTimeDistanceToNowStrict } from '$lib/utils/locale-date';
  import type { LayoutData } from './$types';
  import { RefreshCw } from 'lucide-svelte';
  import { onDestroy } from 'svelte';
  import { trimTrailingZero } from '$lib/utils/number';
  import { page } from '$app/stores';
  import { getCurrentSeasonId, getSeasonName } from '$lib/er/season/season';
  import { getRankTierByQueue } from '$lib/er/rank-tiers/calculation';
  import { error } from '@sveltejs/kit';
  import Adsense from '$components/adsense.svelte';
  import type { ChangeEventHandler } from 'svelte/elements';
  import { browser } from '$app/environment';
  import { getQueueTypeName } from '$lib/er/queue';
  import { erStatic } from '$lib/er/static-all';

  export let data: LayoutData;

  let updateStatus: 'normal' | 'updating' | 'success' | 'fail' = 'normal';

  if (data.player == undefined) {
    throw error(404);
  }

  $: player = data.player!;

  async function requestUpdate() {
    updateStatus = 'updating';
    try {
      await api('POST /player/[id]/update', {
        params: { id: player.id },
      });
      updateStatus = 'success';
      await invalidate((url) => url.pathname.includes('/by-player/'));
    } catch (e) {
      console.error(e);
      updateStatus = 'fail';
    } finally {
      setTimeout(() => {
        updateStatus = 'normal';
      }, 2000);
    }
  }

  $: queue =
    data.seasonId === 'all' && data.queueType === 'all'
      ? player.queues.find((q) => {
          return q.seasonId === getCurrentSeasonId($erStatic?.result.seasons ?? []);
        })
      : player.queues.find((q) => {
          return q.seasonId === data.seasonId;
        });

  $: availableSeasonIds = [...new Set(player.queues.map((v) => v.seasonId)).values()];
  $: availableQueueTypes = [
    ...new Set(
      player.queues
        .filter((q) => (data.seasonId === 'all' ? true : q.seasonId === data.seasonId))
        .map((q) => q.queueType),
    ),
  ];

  $: selectedOrNormalQueue = queue ?? player.queues.find((q) => q.seasonId === 0);
  $: winRateText = selectedOrNormalQueue
    ? trimTrailingZero(
        (selectedOrNormalQueue.totalWins / selectedOrNormalQueue.totalPlays) * 100,
        1,
      ) + '%'
    : $LL.global.notEnoughSamples();

  let intervalId: NodeJS.Timeout;
  $: lastUpdatedAt = getHumanTimeDistanceToNowStrict(player.updatedAt, $locale);
  $: {
    if (browser) {
      clearInterval(intervalId);
      intervalId = setInterval(() => {
        lastUpdatedAt = getHumanTimeDistanceToNowStrict(player.updatedAt, $locale);
      }, 1000);
    }
  }

  const handleSeasonChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newSearchParams = new URLSearchParams($page.url.searchParams);
    newSearchParams.set('season', e.currentTarget.value);
    newSearchParams.set('page', '0');
    goto(`?${newSearchParams.toString()}`);
  };

  const handleQueueTypeChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newSearchParams = new URLSearchParams($page.url.searchParams);
    newSearchParams.set('queue', e.currentTarget.value);
    goto(`?${newSearchParams.toString()}`);
  };

  onDestroy(() => {
    if (browser) {
      clearInterval(intervalId);
    }
  });
</script>

<header>
  <div class="top-area">
    <section class="player-basic-info">
      <RankTierImage type="circle" rankTierName={getRankTierByQueue(queue).tier.tierName} />
      <div class="name-level">
        <h2>{player.name}</h2>
        <div>
          <span class="number">
            LV. {player.level}
          </span>
          <span class="recent-update-label">{$LL.players.recentUpdateDate()}</span>
          <span class="recent-update-date">{lastUpdatedAt}</span>
        </div>
      </div>
    </section>
    <section class="refresh">
      <div>
        <button
          on:click={requestUpdate}
          disabled={updateStatus === 'updating'}
          data-status={updateStatus}
        >
          {#if updateStatus === 'normal'}
            {$LL.players.requestUpdate()}
          {:else if updateStatus === 'fail'}
            갱신 실패
          {:else if updateStatus === 'success'}
            성공! 로드 중...
          {:else if updateStatus === 'updating'}
            <span class="refresh-icon"><RefreshCw /></span>
          {/if}
        </button>
      </div>
    </section>
    <section class="player-basic-stats">
      <section>
        <h3>이번 시즌 {$LL.glossary.winRate()}</h3>
        {#if queue}
          <strong class="number">{winRateText}</strong>
        {:else}
          <strong>{$LL.players.cannotCalculate()}</strong>
        {/if}
      </section>
    </section>
  </div>
  <nav>
    <select class="season-select" on:change={handleSeasonChange} value={data.seasonId}>
      <option value="all">모든 시즌</option>
      {#each availableSeasonIds as seasonId}
        <option value={seasonId}>{getSeasonName($LL, seasonId)}</option>
      {/each}
    </select>
    <select class="queue-type-select" on:change={handleQueueTypeChange} value={data.queueType}>
      <option value="all">모든 큐</option>
      {#each availableQueueTypes as queueType}
        <option value={queueType}>{getQueueTypeName($LL, queueType)}</option>
      {/each}
    </select>
    <a
      href="/{$locale}/players/{player.name}{$page.url.search}"
      class:active={$page.url.pathname.endsWith(`/${encodeURIComponent(player.name)}`)}>개요</a
    >
    <a
      href="/{$locale}/players/{player.name}/characters{$page.url.search}"
      class:active={$page.url.pathname.endsWith('/characters')}>실험체</a
    >
    <a
      href="/{$locale}/players/{player.name}/activities{$page.url.search}"
      class:active={$page.url.pathname.endsWith('/activities')}>활동</a
    >
  </nav>
  <Adsense adSlot="7789559409" horizontal margin="auto" />
</header>
<slot />

<style lang="postcss">
  @import '$lib/styles/breakpoints.css';

  header {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    margin-bottom: var(--space-6);
  }

  .top-area {
    display: flex;
    flex-wrap: wrap;
    place-items: center;
    gap: var(--space-6);
    margin-top: 4rem;
    padding: 0 var(--space-4);
    border-radius: var(--radius-2);

    @media (--sm) {
      padding: 0;
    }
  }

  nav {
    border-bottom: 1px solid var(--border-normal);
    font-size: var(--size-6);
    padding-bottom: var(--space-2);

    a {
      position: relative;
      padding: var(--space-4);
      border-radius: var(--radius-2);
      margin: 0 var(--space-2);
      text-decoration: none;

      &:hover {
        background-color: var(--elevation-2);
      }

      &.active::before {
        content: '';
        position: absolute;
        background: var(--brand);
        left: 50%;
        top: calc(100% + var(--space-2) + 1px);
        width: 100%;
        height: 2px;
        transform: translate(-50%, -50%);
      }
    }
  }

  select {
    /* appearance: none; */
    padding: var(--space-4);
    background-color: transparent;
    border: none;
    color: var(--text);
    margin-right: var(--space-5);

    option {
      color: black;
      background-color: white;
    }
  }

  .player-basic-info {
    display: flex;
    gap: var(--space-4);
    place-items: center;
  }
  .player-basic-info h2 {
    font-size: 2.4rem;
  }
  .player-basic-info :global(img) {
    max-height: 5rem;
    margin-bottom: -6px;
  }
  .name-level {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .player-basic-stats {
    display: flex;
    gap: var(--space-4);
    /* background-color: var(--mauve-a3); */
    padding: var(--space-4);
    border-radius: var(--radius-2);
  }
  .player-basic-stats strong {
    font-size: 2rem;
  }

  .refresh {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: flex-start;
  }
  .refresh button {
    background-color: var(--blue-7);
    color: var(--blue-12);
    font-size: 1.6rem;

    &[data-status='updating'] {
      background-color: var(--blue-6);
    }
    &[data-status='success'] {
      background-color: var(--green-6);
      color: var(--green-12);
    }
    &[data-status='fail'] {
      background-color: var(--red-7);
      color: var(--red-12);
    }
  }
  .refresh button:hover {
    background-color: var(--blue-8);
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .refresh-icon {
    display: block;
    animation-name: spin;
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }

  .recent-update-label {
    margin-left: var(--space-4);
    color: var(--mauve-10);
    margin-right: var(--space-2);
  }
</style>
